import { graphql } from 'gql.tada'
import Router from 'next/router'
import { useSyncExternalStore } from 'react'
import { Client, CombinedError } from 'urql'

import matcher from '@/gql/generated/matcher'

import { GRAPHQL_ENDPOINT } from './env'
import { flutterReady } from './flutter'
import {
  Authentication,
  clearAuthentication,
  getAuthentication,
  makeClientStore,
  saveAuthentication,
  withKeylessTypes,
} from './urql-common'

function isPresent(auth: Authentication | null): auth is Authentication {
  return auth != null
}

const TokenRefreshMutation = graphql(`
  mutation TokenRefreshMutation($fingerprint: String!, $refreshToken: ID!) {
    tokenRefresh(input: { fingerprint: $fingerprint, refreshToken: $refreshToken }) {
      authentication {
        accessToken
        fingerprint
        refreshToken
      }
    }
  }
`)

const AutoSigninMutation = graphql(`
  mutation AutoSigninMutation($email: String!, $password: String!) {
    signIn(input: { email: $email, password: $password }) {
      authentication {
        accessToken
        fingerprint
        refreshToken
      }
    }
  }
`)

export function isContractSignRequiredError(error: unknown): boolean {
  if (!(error instanceof CombinedError)) return false
  return error.graphQLErrors.some((e) => e.extensions.code == 'CONTRACT_SIGN_REQUIRED_ERROR')
}

export function isGuardianRequiredError(error: unknown): boolean {
  if (!(error instanceof CombinedError)) return false
  return error.graphQLErrors.some((e) => e.extensions.code == 'CURRENT_GUARDIAN_REQUIRED_ERROR')
}

export function isPartnerTokenError(error: unknown): boolean {
  if (!(error instanceof CombinedError)) return false
  return error.graphQLErrors.some((e) => e.extensions.code == 'PARTNER_TOKEN_ON_OTHER_SCHEMA')
}

export function isSchoolTokenError(error: unknown): boolean {
  if (!(error instanceof CombinedError)) return false
  return error.graphQLErrors.some((e) => e.extensions.code == 'SCHOOL_TOKEN_ON_OTHER_SCHEMA')
}

function updateSchoolGroupsPagination(cache: any, id?: string) {
  if (id) {
    const queryKey = cache.keyOfEntity({ __typename: 'Query' })
    const currentUserKey = cache.resolve(queryKey, 'currentUser')
    if (currentUserKey) {
      const schoolKey = cache.resolve(currentUserKey, 'school')
      if (schoolKey) {
        const paginationFields = cache.inspectFields(schoolKey)
        paginationFields.forEach((field: any) => {
          if (field.fieldName === 'groupsPagination') {
            cache.invalidate(schoolKey, field.fieldName, field.arguments || {})
          }
        })
      }
    }
  } else {
    cache.invalidate('SchoolGroup')
  }
}

function updateSchoolStudentsPagination(cache: any, id?: string) {
  if (id) {
    const queryKey = cache.keyOfEntity({ __typename: 'Query' })
    const currentUserKey = cache.resolve(queryKey, 'currentUser')
    if (currentUserKey) {
      const schoolKey = cache.resolve(currentUserKey, 'school')
      if (schoolKey) {
        const paginationFields = cache.inspectFields(schoolKey)
        paginationFields.forEach((field: any) => {
          if (field.fieldName === 'studentsPagination') {
            cache.invalidate(schoolKey, field.fieldName, field.arguments || {})
          }
        })
      }
    }
  } else {
    cache.invalidate('SchoolUser')
  }
}

function updateSchoolTeachersPagination(cache: any, id?: string) {
  if (id) {
    const queryKey = cache.keyOfEntity({ __typename: 'Query' })
    const currentUserKey = cache.resolve(queryKey, 'currentUser')
    if (currentUserKey) {
      const schoolKey = cache.resolve(currentUserKey, 'school')
      if (schoolKey) {
        const paginationFields = cache.inspectFields(schoolKey)
        paginationFields.forEach((field: any) => {
          if (field.fieldName === 'teachersPagination') {
            cache.invalidate(schoolKey, field.fieldName, field.arguments || {})
          }
        })
      }
    }
  } else {
    cache.invalidate('SchoolUser')
  }
}

const { getSnapshot, resetClient, subscribe } = makeClientStore(GRAPHQL_ENDPOINT, {
  authExchangeOptions: {
    anonymousFields: new Set([
      'signIn',
      'tokenRefresh',
      'invite',
      'invitationRedeem',
      'initialPasswordSetup',
      'signUp',
      'latestContracts',
      'latestContract',
      'latestTermOfSaleContract',
      'latestCookieRegulationContract',
      'passwordForgot',
      'passwordForgotConfirm',
      'licenseCodeCheck',
      'promotionCodeCheck',
      'pricingInfo',
      'stripeSubscriptionDefaultTrailEndInfo',
      'stripeSubscriptionPrecreate',
      'stripeSubscriptionPrecreateWithInviteToken',
      'schoolGuardianUserSignUpWithInviteToken',
      'stripeSubscriptionPrecreateWithPartnerCode',
      'demoSubscriptionCreate',
      'latestAppVersions',
      'freeTrialSignUp',
      'schoolCodeCheck',
      'schoolUserCheck',
      'schoolGuardianUserSignUp',
      'schoolGuardianUserInviteTokenValidate',
      'partnerUserSignUpWithPartnerCode',
      'challenge',
      'challengeUserSignUp',
      'partnerCodeValidate',
      '__schema',
    ]),
    refreshAuth: async (mutate) => {
      let authentication = getAuthentication()
      if (authentication) {
        const result = await mutate(TokenRefreshMutation, {
          fingerprint: authentication.fingerprint,
          refreshToken: authentication.refreshToken,
        })
        saveAuthentication(result.data?.tokenRefresh?.authentication ?? null)
        authentication = getAuthentication()
      }

      // refresh token successed
      if (isPresent(authentication)) return

      await flutterReady
      const loginInfo = await window.flutter_inappwebview.callHandler('getUserInfo')

      if (loginInfo) {
        // try relogin
        const result = await mutate(AutoSigninMutation, {
          email: loginInfo.username,
          password: loginInfo.password,
        })
        saveAuthentication(result.data?.signIn?.authentication ?? null)
        authentication = getAuthentication()
        // login successed
        if (isPresent(authentication)) return
      }

      clearAuthentication()
      await logout()
    },
  },
  cacheExchangeOptions: {
    possibleTypes: matcher.possibleTypes,
    updates: {
      Mutation: {
        guardianSelect(result: any, _, cache) {
          cache.link('Query', 'currentGuardian', result?.guardianSelect?.guardian)
        },
        profileSetup(result: any, _, cache) {
          const id = result.profileSetup?.viewer?.id
          if (!id) return
          cache.link('Query', 'currentUser', cache.keyOfEntity({ __typename: 'User', id }))
        },
        guardianDelete(result: any, __, cache) {
          const id = result.guardianDelete?.guardian?.id
          if (id) cache.invalidate({ __typename: 'Guardian', id })
          else cache.invalidate('Guardian')
        },
        playerDelete(result: any, __, cache) {
          const id = result.playerDelete?.player?.id
          if (id) cache.invalidate({ __typename: 'Player', id })
          else cache.invalidate('Player')
        },
        emptyPlayerCreate(result: any, __, cache) {
          const id = result.emptyPlayerCreate?.player?.id
          if (!id) return
          const currentUserKey = cache.resolve('Query', 'currentUser') as string
          cache.invalidate(currentUserKey, 'players')
        },
        playerHearingInformationUpdate(_, args: { input: { playerId: string } }, cache) {
          cache.invalidate({ __typename: 'Player', id: args.input.playerId }, 'aiChatMessages')
        },
        skillStepSetMastered(_, args: { input: { playerId: string } }, cache) {
          const playerEntity = {
            __typename: 'Player',
            id: args.input.playerId,
          }
          cache
            .inspectFields(playerEntity)
            .filter((x) => x.fieldName == 'playerLevel')
            .forEach((x) => cache.invalidate(playerEntity, x.fieldName, x.arguments))
        },
        playerSkillLevelsAdjust(_, args: { input: { playerId: string } }, cache) {
          const playerEntity = {
            __typename: 'Player',
            id: args.input.playerId,
          }
          cache
            .inspectFields(playerEntity)
            .filter((x) => x.fieldName == 'playerLevel')
            .forEach((x) => cache.invalidate(playerEntity, x.fieldName, x.arguments))
        },
        schoolStudentDelete(result: any, __, cache) {
          const id = result.schoolStudentDelete?.deletedStudentId
          if (id) cache.invalidate({ __typename: 'SchoolUser', id })
          else cache.invalidate('SchoolUser')
        },
        schoolTeacherDelete(result: any, __, cache) {
          const id = result.schoolTeacherDelete?.deletedTeacherId
          if (id) cache.invalidate({ __typename: 'SchoolUser', id })
          else cache.invalidate('SchoolUser')
        },
        schoolGroupDelete(result: any, __, cache) {
          const id = result.schoolGroupDelete?.deletedSchoolGroupId
          updateSchoolGroupsPagination(cache, id)
        },
        schoolGroupCreate(result: any, __, cache) {
          const id = result.schoolGroupCreate?.schoolGroup?.id
          updateSchoolGroupsPagination(cache, id)
        },
        schoolStudentCreate(result: any, __, cache) {
          const id = result.schoolStudentCreate?.student?.id
          updateSchoolStudentsPagination(cache, id)
        },
        schoolStudentBatchCreate(result: any, __, cache) {
          const id = result.schoolStudentBatchCreate?.school?.id
          updateSchoolStudentsPagination(cache, id)
        },
        schoolTeacherCreate(result: any, __, cache) {
          const id = result.schoolTeacherCreate?.teacher?.id
          updateSchoolTeachersPagination(cache, id)
        },
        schoolTeacherBatchCreate(result: any, __, cache) {
          const id = result.schoolTeacherBatchCreate?.school?.id
          updateSchoolTeachersPagination(cache, id)
        },
      },
    },
    keys: withKeylessTypes([
      'AppVersions',
      'PlayerPrimarySkillLevel',
      'AssessmentScore',
      'LearningStatusHeatmap',
      'LearningTimeTransition',
      'LearningTimeVisualization',
      'LearningTimeData',
      'ScoreHistory',
      'AssessmentScoreHistory',
      'CardPaymentMethod',
      'PricingInfo',
      'PlayerPrimarySkillLevelHistory',
      'DailyLearningParameter',
      'PlayerTertiarySkillLevel',
      'PlayerAvatar',
      'MotivationPointVisualization',
      'LearningReport',
      'WeeklyLearningTimeSetting',
    ]),
  },
  isErrorHandledInBoundary: (error: unknown) =>
    isContractSignRequiredError(error) ||
    isGuardianRequiredError(error) ||
    isPartnerTokenError(error) ||
    isSchoolTokenError(error),
})

export async function logout(redirectUrl?: string) {
  clearAuthentication()

  const defaultRedirectUrl = Router.pathname.match(/^\/web\/?/)?.[0]
    ? '/web/sign_in'
    : '/app/sign_in'

  await Router.push(redirectUrl ?? defaultRedirectUrl)
  resetClient()

  // call `uLogin` flutter handler
  flutterReady.then(() => window.flutter_inappwebview.callHandler('uLogout'))
}

export function resetUrqlClient() {
  resetClient()
}

export function useUrqlClient(): Client {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function getCurrentClient(): Client {
  return getSnapshot()
}
